import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { TemplatesProvider } from './context/TemplatesContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ToastContainer from './components/common/ToastContainer';
import { prefetchAllIdle, prefetchRoute } from './utils/routePreload';

// 用 prefetchRoute 來「順便」把 lazy 用到的 chunk 註冊起來，這樣 Header 的 PrefetchLink
// 在 hover 時觸發的 import() 和 React.lazy 用的是同一個 promise，瀏覽器只下載一次。
const lazyRoute = (path) => lazy(() => prefetchRoute(path));

const Login = lazy(() => import('./pages/Login'));
const QuotationForm = lazyRoute('/create');
const QuotationList = lazyRoute('/quotations');
const QuotationDetail = lazyRoute('/quotations/:id');
const ShippingDocumentPage = lazyRoute('/shipping/:id');
const InvoiceList = lazyRoute('/invoices');
const InvoiceDetail = lazyRoute('/invoices/:id');
const MessageTemplatesSettings = lazyRoute('/message-templates');
const Dashboard = lazyRoute('/dashboard');
const CompanyManagement = lazyRoute('/companies');
const UserManagement = lazyRoute('/users');
const UserProfile = lazyRoute('/profile');
const NotFound = lazy(() => import('./pages/NotFound'));

const CustomerList = lazyRoute('/customers');
const CustomerDetail = lazyRoute('/customers/:id');
const CustomerForm = lazyRoute('/customers/new');

const AssetList = lazyRoute('/assets');
const AssetForm = lazyRoute('/assets/new');
const AssetDetail = lazyRoute('/assets/:id');

const ScheduleCalendar = lazyRoute('/schedule');
const DashboardSimple = lazyRoute('/');
const CompanySettings = lazyRoute('/company-settings');

const SignaturePage = lazy(() => import('./pages/SignaturePage'));

const LoadingFallback = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>載入中...</p>
  </div>
);

// RENT-50：離開資產樹（/assets, /assets/new, /assets/:id, /assets/:id/edit）到非資產
// 樹的路徑時，清掉 AssetList 的 sessionStorage 篩選紀錄。Bridget 2026-06-03 補充：
// 「列表 ↔ 查看/編輯」之間要保留篩選，但從資產表離開到報價單等其他頁面後再回來，
// 預期是未篩選狀態。AssetList.js 仍負責讀寫 sessionStorage，此處只負責「離開時清除」。
const isInAssetTree = (pathname) => pathname === '/assets' || pathname.startsWith('/assets/');

const AssetFiltersLifecycle = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    if (isInAssetTree(prev) && !isInAssetTree(next)) {
      try {
        sessionStorage.removeItem('assetList:filters');
      } catch {}
    }
    prevPathRef.current = next;
  }, [location.pathname]);
  return null;
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  const isSignaturePage = location.pathname.startsWith('/signature/');

  return (
    <div className="App">
      {!isSignaturePage && <Header />}
      <main className={isSignaturePage ? "signature-container" : "container"}>
        {children}
      </main>
      {!isSignaturePage && <Footer />}
      <ToastContainer />
    </div>
  );
};

const IdlePrefetch = () => {
  useEffect(() => {
    prefetchAllIdle();
  }, []);
  return null;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <TemplatesProvider>
          <ToastProvider>
            <AppLayout>
              <AssetFiltersLifecycle />
              <IdlePrefetch />
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Login />
                  </Suspense>
                } />

                {/* Signature page - Public access with token */}
                <Route path="/signature/:token" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <SignaturePage />
                  </Suspense>
                } />

                {/* Protected Routes - All authenticated users */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <DashboardSimple />
                    </Suspense>
                  } />
                  <Route path="/profile" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserProfile />
                    </Suspense>
                  } />
                  <Route path="/dashboard" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Dashboard />
                    </Suspense>
                  } />
                  <Route path="/quotations" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationList />
                    </Suspense>
                  } />
                  <Route path="/quotations/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationDetail />
                    </Suspense>
                  } />
                  <Route path="/shipping/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ShippingDocumentPage />
                    </Suspense>
                  } />
                  <Route path="/create" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationForm key="create" />
                    </Suspense>
                  } />
                  <Route path="/edit/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationForm key="edit" />
                    </Suspense>
                  } />
                  <Route path="/invoices" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InvoiceList />
                    </Suspense>
                  } />
                  <Route path="/invoices/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InvoiceDetail />
                    </Suspense>
                  } />

                  {/* Customer Management Routes */}
                  <Route path="/customers" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerList />
                    </Suspense>
                  } />
                  <Route path="/customers/new" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerForm />
                    </Suspense>
                  } />
                  <Route path="/customers/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerDetail />
                    </Suspense>
                  } />
                  <Route path="/customers/:id/edit" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerForm />
                    </Suspense>
                  } />

                  {/* Asset Management Routes */}
                  <Route path="/assets" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetList />
                    </Suspense>
                  } />
                  <Route path="/assets/new" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetForm />
                    </Suspense>
                  } />
                  <Route path="/assets/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetDetail />
                    </Suspense>
                  } />
                  <Route path="/assets/:id/edit" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetForm />
                    </Suspense>
                  } />
                  {/* Dashboard Simple Route */}
                  <Route path="/dashboard-simple" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <DashboardSimple />
                    </Suspense>
                  } />

                  {/* Schedule Calendar Route */}
                  <Route path="/schedule" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ScheduleCalendar />
                    </Suspense>
                  } />
                </Route>

                {/* Admin-only Routes */}
                <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
                  <Route path="/message-templates" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <MessageTemplatesSettings />
                    </Suspense>
                  } />
                  <Route path="/company-settings" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CompanySettings />
                    </Suspense>
                  } />
                </Route>

                {/* Superadmin-only Routes */}
                <Route element={<ProtectedRoute allowedRoles={['superadmin']} />}>
                  <Route path="/companies" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CompanyManagement />
                    </Suspense>
                  } />
                  <Route path="/users" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserManagement />
                    </Suspense>
                  } />
                </Route>

                {/* User Management - Accessible by superadmin and admin */}
                <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
                  <Route path="/companies/:companyId/users" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserManagement />
                    </Suspense>
                  } />
                </Route>

                {/* Not Found */}
                <Route path="*" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <NotFound />
                  </Suspense>
                } />
              </Routes>
            </AppLayout>
          </ToastProvider>
        </TemplatesProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
